import React from "react";
import { BRANCHES } from "../data/content";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const ROWS = [
  { label: "Mission", key: "mission" },
  { label: "Enlistment term", key: "enlistmentTerm" },
  { label: "Basic training", key: "basicTraining" },
];

export default function Compare() {
  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Branch comparison</p>
      <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-3">
        See the differences at a glance
      </h1>
      <p className="text-lg text-slate-700 mt-5 max-w-2xl leading-relaxed">
        Mission, time commitment, and where you{`'`}ll train — side by side for all six branches.
      </p>

      {/* DESKTOP TABLE */}
      <div className="mt-12 overflow-x-auto rounded-xl border border-slate-200 hidden md:block" data-testid="compare-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-4">Feature</th>
              {BRANCHES.map((b) => (
                <th key={b.slug} className="text-left px-5 py-4">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: b.color }} />
                    <span className="font-display font-bold text-slate-900">{b.name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row, i) => (
              <tr key={row.key} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                <td className="px-5 py-4 font-semibold text-slate-700 align-top w-44">{row.label}</td>
                {BRANCHES.map((b) => (
                  <td key={b.slug} className="px-5 py-4 text-slate-700 align-top leading-relaxed min-w-[180px]">
                    {b[row.key]}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-white border-t border-slate-200">
              <td className="px-5 py-4 font-semibold text-slate-700 align-top">Notable jobs</td>
              {BRANCHES.map((b) => (
                <td key={b.slug} className="px-5 py-4 align-top min-w-[180px]">
                  <ul className="space-y-1 text-xs text-slate-600">
                    {b.notableJobs.map((j) => <li key={j}>• {j}</li>)}
                  </ul>
                </td>
              ))}
            </tr>
            <tr className="bg-slate-50/50 border-t border-slate-200">
              <td className="px-5 py-4 font-semibold text-slate-700 align-top"></td>
              {BRANCHES.map((b) => (
                <td key={b.slug} className="px-5 py-4 align-top">
                  <Link
                    to={`/branches/${b.slug}`}
                    data-testid={`compare-detail-${b.slug}`}
                    className="inline-flex items-center gap-1 text-[#0033A0] font-semibold text-sm hover:underline"
                  >
                    Details <ArrowRight size={14} weight="bold" />
                  </Link>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="mt-10 grid grid-cols-1 gap-4 md:hidden">
        {BRANCHES.map((b) => (
          <div key={b.slug} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="h-2" style={{ background: b.color }} />
            <div className="p-5">
              <h3 className="font-display text-xl font-bold text-slate-900">{b.name}</h3>
              <div className="mt-3 space-y-2 text-sm">
                {ROWS.map((r) => (
                  <div key={r.key}>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{r.label}</span>
                    <p className="text-slate-700 mt-0.5 leading-relaxed">{b[r.key]}</p>
                  </div>
                ))}
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Notable jobs</span>
                  <ul className="mt-1 text-slate-700 text-sm space-y-0.5">
                    {b.notableJobs.map((j) => <li key={j}>• {j}</li>)}
                  </ul>
                </div>
              </div>
              <Link to={`/branches/${b.slug}`} className="mt-4 inline-flex items-center gap-1 text-[#0033A0] font-semibold text-sm">
                Full details <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-8 leading-relaxed">
        Information above is summarized from publicly available official sources. Enlistment terms and training locations can change — confirm with a recruiter for the latest details.
      </p>
    </main>
  );
}
